import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CountryModel, CountryData } from 'shared/models';

export interface MobileValidationResult {
  isValid: boolean;
  error?: string;
  formattedNumber?: string;
  country?: CountryModel;
}

@Injectable({
  providedIn: 'root'
})
export class MobileValidationService {

  // Common dummy/test numbers to reject
  private readonly DUMMY_NUMBERS = [
    '0000000000',
    '1111111111',
    '2222222222',
    '3333333333',
    '4444444444',
    '5555555555',
    '6666666666',
    '7777777777',
    '8888888888',
    '9999999999',
    '1234567890',
    '0987654321',
    '1122334455',
    '9876543210'
  ];

  constructor() { }

  /**
   * Validates a mobile number for a specific country
   */
  validateMobileNumber(mobileNumber: string, country: CountryModel): MobileValidationResult {
    if (!mobileNumber || !country) {
      return {
        isValid: false,
        error: 'Mobile number and country are required'
      };
    }

    // Clean the mobile number (remove spaces, dashes, etc.)
    const cleanNumber = this.cleanMobileNumber(mobileNumber);

    // Check if it's a dummy number
    if (this.isDummyNumber(cleanNumber)) {
      return {
        isValid: false,
        error: 'Please enter a valid mobile number (dummy numbers are not allowed)'
      };
    }

    // Validate digit count
    if (cleanNumber.length !== country.digitCount) {
      return {
        isValid: false,
        error: `Mobile number must be exactly ${country.digitCount} digits for ${country.countryName}`
      };
    }

    // Validate format based on country-specific rules
    const formatValidation = this.validateCountrySpecificFormat(cleanNumber, country);
    if (!formatValidation.isValid) {
      return formatValidation;
    }

    // Format the number with country code
    const formattedNumber = this.formatMobileNumber(cleanNumber, country);

    return {
      isValid: true,
      formattedNumber: formattedNumber,
      country: country
    };
  }

  /**
   * Validates a full international mobile number (with country code)
   */
  validateInternationalNumber(fullNumber: string): MobileValidationResult {
    if (!fullNumber) {
      return {
        isValid: false,
        error: 'Mobile number is required'
      };
    }

    // Remove any non-digit characters except +
    const cleanNumber = fullNumber.replace(/[^\d+]/g, '');

    // Find matching country by dial code
    let matchedCountry: CountryModel | undefined;

    for (const country of CountryData.countries) {
      const dialCode = country.dialCode.replace('+', '');
      if (cleanNumber.startsWith('+' + dialCode) || cleanNumber.startsWith(dialCode)) {
        matchedCountry = country;
        break;
      }
    }

    if (!matchedCountry) {
      return {
        isValid: false,
        error: 'Invalid country code'
      };
    }

    // Extract the local number part
    const dialCodeDigits = matchedCountry.dialCode.replace('+', '');
    let localNumber = cleanNumber;

    if (localNumber.startsWith('+')) {
      localNumber = localNumber.substring(dialCodeDigits.length + 1);
    } else if (localNumber.startsWith(dialCodeDigits)) {
      localNumber = localNumber.substring(dialCodeDigits.length);
    }

    // Validate the local number
    return this.validateMobileNumber(localNumber, matchedCountry);
  }

  /**
   * Creates an Angular validator function for mobile numbers
   */
  createMobileValidator(country: CountryModel): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Don't validate empty values (let required validator handle it)
      }

      const result = this.validateMobileNumber(control.value, country);

      if (!result.isValid) {
        return {
          invalidMobileNumber: {
            message: result.error,
            country: country.countryName
          }
        };
      }

      return null;
    };
  }

  /**
   * Creates an Angular validator function for international mobile numbers
   */
  createInternationalMobileValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Don't validate empty values
      }

      const result = this.validateInternationalNumber(control.value);

      if (!result.isValid) {
        return {
          invalidInternationalMobileNumber: {
            message: result.error
          }
        };
      }

      return null;
    };
  }

  /**
   * Formats a mobile number with country code
   */
  formatMobileNumber(localNumber: string, country: CountryModel): string {
    const cleanNumber = this.cleanMobileNumber(localNumber);
    return `${country.dialCode}${cleanNumber}`;
  }

  /**
   * Formats a mobile number for display (with spaces)
   */
  formatMobileNumberForDisplay(localNumber: string, country: CountryModel): string {
    const cleanNumber = this.cleanMobileNumber(localNumber);

    // Apply country-specific formatting
    switch (country.countryCode) {
      case 'IN':
        return `${country.dialCode} ${cleanNumber.substring(0, 5)} ${cleanNumber.substring(5)}`;
      case 'US':
      case 'CA':
        return `${country.dialCode} ${cleanNumber.substring(0, 3)} ${cleanNumber.substring(3, 6)} ${cleanNumber.substring(6)}`;
      case 'GB':
        return `${country.dialCode} ${cleanNumber.substring(0, 4)} ${cleanNumber.substring(4)}`;
      default:
        // Generic formatting
        const half = Math.ceil(cleanNumber.length / 2);
        return `${country.dialCode} ${cleanNumber.substring(0, half)} ${cleanNumber.substring(half)}`;
    }
  }

  /**
   * Cleans a mobile number by removing non-digit characters
   */
  private cleanMobileNumber(mobileNumber: string): string {
    return mobileNumber.replace(/\D/g, '');
  }

  /**
   * Checks if a number is a common dummy/test number
   */
  private isDummyNumber(cleanNumber: string): boolean {
    return this.DUMMY_NUMBERS.includes(cleanNumber);
  }

  /**
   * Validates country-specific mobile number formats
   */
  private validateCountrySpecificFormat(cleanNumber: string, country: CountryModel): MobileValidationResult {
    switch (country.countryCode) {
      case 'IN':
        // Indian mobile numbers: should start with 6, 7, 8, or 9
        if (!/^[6-9]/.test(cleanNumber)) {
          return {
            isValid: false,
            error: 'Indian mobile numbers must start with 6, 7, 8, or 9'
          };
        }
        break;

      case 'US':
      case 'CA':
        // US/Canada: area code cannot start with 0 or 1
        if (/^[01]/.test(cleanNumber)) {
          return {
            isValid: false,
            error: 'Area code cannot start with 0 or 1'
          };
        }
        break;

      case 'GB':
        // UK mobile numbers: should start with 7
        if (!/^7/.test(cleanNumber)) {
          return {
            isValid: false,
            error: 'UK mobile numbers must start with 7'
          };
        }
        break;

      case 'AU':
        // Australian mobile numbers: should start with 4
        if (!/^4/.test(cleanNumber)) {
          return {
            isValid: false,
            error: 'Australian mobile numbers must start with 4'
          };
        }
        break;

      case 'DE':
        // German mobile numbers: should start with 15, 16, or 17
        if (!/^1[567]/.test(cleanNumber)) {
          return {
            isValid: false,
            error: 'German mobile numbers must start with 15, 16, or 17'
          };
        }
        break;

      // Add more country-specific validations as needed
      default:
        // Generic validation - just check that it's all digits
        if (!/^\d+$/.test(cleanNumber)) {
          return {
            isValid: false,
            error: 'Mobile number must contain only digits'
          };
        }
        break;
    }

    return { isValid: true };
  }

  /**
   * Gets a country by its name (case-insensitive)
   */
  getCountryByName(countryName: string): CountryModel | undefined {
    return CountryData.getCountryByName(countryName);
  }

  /**
   * Creates a mobile validator that works with country name from form
   */
  createMobileValidatorForCountryName(getCountryName: () => string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Don't validate empty values
      }

      const countryName = getCountryName();
      if (!countryName) {
        return null; // Don't validate if no country selected
      }

      const country = this.getCountryByName(countryName);
      if (!country) {
        return null; // Don't validate if country not found
      }

      const result = this.validateMobileNumber(control.value, country);

      if (!result.isValid) {
        return {
          invalidMobileNumber: {
            message: result.error,
            country: country.countryName
          }
        };
      }

      return null;
    };
  }

  /**
   * Gets all available countries
   */
  getAllCountries(): CountryModel[] {
    return CountryData.countries;
  }

  /**
   * Gets the default country (India)
   */
  getDefaultCountry(): CountryModel {
    return CountryData.getDefaultCountry();
  }
}
